import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  ExternalLink,
  Search,
  Globe,
  List,
  Pencil,
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  Loader,
} from 'lucide-react'

export interface KeywordDetailData {
  id: number
  keyword: string
  position: number | null
  manualPosition: number | null
  url: string | null
  checkedAt: string | null
  device: string
  location: string
}

interface SearchSettings {
  country: string
  language: string
}

interface SerpItem {
  position: number
  url: string
  title: string
  description: string
  domain: string
}

interface KeywordDetailModalProps {
  keyword: KeywordDetailData
  searchSettings?: SearchSettings
  projectId: string
  onClose: () => void
  onSaveManualPosition: (id: number, position: number | null) => Promise<void>
}

function buildGoogleUrl(kw: string, country: string, language: string): string {
  const q = encodeURIComponent(kw)
  const lang = language || 'fr'
  const gl = (country || 'FR').toLowerCase()
  return `https://www.google.com/search?q=${q}&hl=${lang}&gl=${gl}&ie=UTF-8`
}

function formatDateFull(iso: string | null): string {
  if (!iso) return '--'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function truncateDomain(url: string): string {
  try {
    const u = new URL(url)
    return (u.hostname + u.pathname).replace(/\/$/, '').slice(0, 55)
  } catch {
    return url.slice(0, 55)
  }
}

const PAGE_SIZE = 10

// Google-style SERP result renderer
function SerpPanel({
  items,
  checkedAt,
  targetUrl,
  keyword,
  onBack,
}: {
  items: SerpItem[]
  checkedAt: string
  targetUrl: string | null
  keyword: string
  onBack: () => void
}) {
  const [page, setPage] = useState(1)
  const scrollRef = useRef<HTMLDivElement>(null)

  const totalPages = Math.ceil(items.length / PAGE_SIZE)
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const targetDomain = targetUrl
    ? (() => { try { return new URL(targetUrl).hostname.replace(/^www\./, '') } catch { return '' } })()
    : ''

  // Which page contains our site
  const targetPage = (() => {
    const idx = items.findIndex(i => targetDomain && (i.domain === targetDomain || i.domain.endsWith(`.${targetDomain}`)))
    return idx >= 0 ? Math.ceil((idx + 1) / PAGE_SIZE) : null
  })()

  function goToPage(p: number) {
    setPage(p)
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sub-header */}
      <div className="px-5 py-3 border-b border-[#E2E4ED] flex items-center gap-2.5 shrink-0">
        <button
          onClick={onBack}
          className="p-1 rounded-lg text-[#8B8FA7] hover:text-[#1B2559] hover:bg-[#F4F5FA] transition-colors"
        >
          <ChevronLeft size={16} strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#1B2559]">Snapshot SERP</p>
          <p className="text-[10px] text-[#8B8FA7]">
            {formatDateShort(checkedAt)} · {items.length} résultats
            {targetPage && (
              <span className="ml-1.5 text-orion-primary font-medium">
                · Votre site page {targetPage}
              </span>
            )}
          </p>
        </div>
        {targetPage && targetPage !== page && (
          <button
            onClick={() => goToPage(targetPage)}
            className="text-[10px] font-medium text-orion-primary bg-blue-50 hover:bg-blue-100 rounded-lg px-2 py-1 transition-colors shrink-0"
          >
            Aller à la page {targetPage}
          </button>
        )}
      </div>

      {/* Google search bar */}
      <div className="px-5 py-2.5 bg-[#F8F9FC] border-b border-[#E2E4ED] shrink-0">
        <div className="flex items-center gap-2 bg-white border border-[#E2E4ED] rounded-full px-3 py-1.5 shadow-sm max-w-lg">
          <Search size={13} className="text-[#8B8FA7] shrink-0" strokeWidth={1.25} />
          <span className="text-sm text-[#1B2559] truncate">{keyword}</span>
        </div>
        <p className="text-[10px] text-[#8B8FA7] mt-1.5 pl-1">
          Page {page} sur {totalPages} · résultats {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, items.length)}
        </p>
      </div>

      {/* Results — scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-1">
        {pageItems.map((item) => {
          const isTarget = targetDomain && (item.domain === targetDomain || item.domain.endsWith(`.${targetDomain}`))
          return (
            <div
              key={item.position}
              className={`py-3 border-b border-[#F4F5FA] last:border-0 ${isTarget ? 'bg-blue-50/50 -mx-5 px-5' : ''}`}
            >
              {/* Position + breadcrumb line */}
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className={`text-[10px] font-bold tabular-nums rounded px-1.5 py-0.5 shrink-0 ${
                  isTarget ? 'bg-orion-primary text-white' : 'bg-[#E2E4ED] text-[#555A73]'
                }`}>
                  {item.position}
                </span>
                <span className="text-[10px] text-[#34A853] truncate flex-1">
                  {truncateDomain(item.url)}
                </span>
                {isTarget && (
                  <span className="text-[9px] font-semibold text-orion-primary bg-blue-100 rounded-full px-1.5 py-0.5 shrink-0">
                    Votre site
                  </span>
                )}
              </div>
              {/* Title */}
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[14px] font-medium text-[#1a0dab] hover:underline leading-snug line-clamp-1 block"
              >
                {item.title || item.url}
              </a>
              {/* Snippet */}
              {item.description && (
                <p className="text-[12px] text-[#545454] leading-relaxed line-clamp-2 mt-0.5">
                  {item.description}
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-[#E2E4ED] flex items-center justify-center gap-1 shrink-0">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
            className="px-2.5 py-1.5 rounded-lg text-xs text-[#555A73] hover:bg-[#F4F5FA] disabled:opacity-30 transition-colors"
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={`min-w-[32px] py-1.5 rounded-lg text-xs font-medium transition-colors ${
                p === page
                  ? 'bg-orion-primary text-white'
                  : targetPage === p
                    ? 'bg-blue-100 text-orion-primary border border-blue-200'
                    : 'text-[#555A73] hover:bg-[#F4F5FA]'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            className="px-2.5 py-1.5 rounded-lg text-xs text-[#555A73] hover:bg-[#F4F5FA] disabled:opacity-30 transition-colors"
          >
            →
          </button>
        </div>
      )}
    </div>
  )
}

export default function KeywordDetailModal({
  keyword,
  searchSettings,
  projectId,
  onClose,
  onSaveManualPosition,
}: KeywordDetailModalProps) {
  const [manualEnabled, setManualEnabled] = useState(keyword.manualPosition !== null)
  const [manualValue, setManualValue] = useState<string>(
    keyword.manualPosition !== null ? String(keyword.manualPosition) : ''
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [view, setView] = useState<'main' | 'serp'>('main')
  const [serpData, setSerpData] = useState<{ items: SerpItem[]; checkedAt: string } | null>(null)
  const [serpLoading, setSerpLoading] = useState(false)
  const [serpError, setSerpError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const country = searchSettings?.country || 'FR'
  const language = searchSettings?.language || 'fr'
  const googleUrl = buildGoogleUrl(keyword.keyword, country, language)

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view === 'serp') setView('main')
        else onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose, view])

  useEffect(() => {
    if (manualEnabled) setTimeout(() => inputRef.current?.focus(), 50)
  }, [manualEnabled])

  async function loadSerpSnapshot() {
    setSerpLoading(true)
    setSerpError(null)
    try {
      const res = await fetch(`/api/keywords/${keyword.id}/serp-snapshot`, { credentials: 'include' })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Erreur')
      if (!json.data) {
        setSerpError('Aucun snapshot disponible pour ce mot-clé. Relancez un check pour en générer un.')
        return
      }
      setSerpData({ items: json.data.items as SerpItem[], checkedAt: json.data.checkedAt })
      setView('serp')
    } catch (err: any) {
      setSerpError(err.message)
    } finally {
      setSerpLoading(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    try {
      const pos = manualEnabled ? (parseInt(manualValue, 10) || null) : null
      await onSaveManualPosition(keyword.id, pos)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err: any) {
      setSaveError(err.message || 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="absolute inset-0 bg-[#0F1728]/60 backdrop-blur-sm" />

      <div className={`relative w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-200 ${
        view === 'serp' ? 'max-w-2xl h-[85vh]' : 'max-w-md'
      }`}>

        {view === 'serp' && serpData ? (
          <SerpPanel
            items={serpData.items}
            checkedAt={serpData.checkedAt}
            targetUrl={keyword.url}
            keyword={keyword.keyword}
            onBack={() => setView('main')}
          />
        ) : (
          <>
            {/* Header */}
            <div className="bg-[#1B2559] px-5 py-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] text-[#8B8FA7] uppercase tracking-wider mb-1">Mot-clé</p>
                <h2 className="text-white font-semibold text-base leading-tight break-words">
                  {keyword.keyword}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="shrink-0 p-1 rounded-lg text-[#8B8FA7] hover:text-white hover:bg-white/10 transition-colors mt-0.5"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-3">
              {/* Position info */}
              <div className="mb-4 flex items-center gap-3">
                <div className="text-center bg-[#F4F5FA] rounded-xl px-4 py-2.5 min-w-[64px]">
                  <p className="text-[10px] text-[#8B8FA7] mb-0.5">Position</p>
                  <p className="text-2xl font-bold text-[#1B2559] tabular-nums">
                    {keyword.position ?? '--'}
                  </p>
                </div>
                {keyword.manualPosition !== null && (
                  <div className="text-center bg-amber-50 border border-amber-200/60 rounded-xl px-4 py-2.5 min-w-[64px]">
                    <p className="text-[10px] text-amber-600 mb-0.5">Manuelle</p>
                    <p className="text-2xl font-bold text-amber-700 tabular-nums">
                      {keyword.manualPosition}
                    </p>
                  </div>
                )}
                <div className="flex-1 text-right">
                  <p className="text-[10px] text-[#8B8FA7] uppercase tracking-wider">Dernier check</p>
                  <p className="text-xs text-[#555A73] mt-0.5">{formatDateFull(keyword.checkedAt)}</p>
                </div>
              </div>

              {/* Info rows */}
              <div className="mb-4 divide-y divide-[#F4F5FA]">
                {/* URL classée */}
                {keyword.url && (
                  <div className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2.5 text-sm text-[#555A73] min-w-0">
                      <Globe size={14} strokeWidth={1.25} className="text-[#8B8FA7] shrink-0" />
                      <span className="truncate text-xs">{keyword.url.replace(/^https?:\/\//, '').replace(/\/$/, '').slice(0, 42)}</span>
                    </div>
                    <a
                      href={keyword.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-orion-primary hover:text-orion-primary-hover transition-colors shrink-0 ml-3"
                    >
                      Ouvrir <ExternalLink size={11} strokeWidth={1.5} />
                    </a>
                  </div>
                )}

                {/* Google live */}
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-[#555A73]">
                    <Search size={14} strokeWidth={1.25} className="text-[#8B8FA7]" />
                    Résultats en direct
                  </div>
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-orion-primary hover:text-orion-primary-hover transition-colors"
                  >
                    Google <ExternalLink size={11} strokeWidth={1.5} />
                  </a>
                </div>

                {/* SERP snapshot */}
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-[#555A73]">
                    <List size={14} strokeWidth={1.25} className="text-[#8B8FA7]" />
                    <span>
                      Snapshot SERP
                      <span className="ml-1.5 text-[10px] text-[#8B8FA7]">(100 résultats au dernier check)</span>
                    </span>
                  </div>
                  <button
                    onClick={loadSerpSnapshot}
                    disabled={serpLoading}
                    className="inline-flex items-center gap-1 text-xs font-medium text-orion-primary hover:text-orion-primary-hover transition-colors disabled:opacity-50"
                  >
                    {serpLoading ? (
                      <Loader size={11} strokeWidth={1.5} className="animate-spin" />
                    ) : null}
                    Voir
                  </button>
                </div>

                {serpError && (
                  <p className="py-1.5 text-xs text-[#8B8FA7] italic pl-6">{serpError}</p>
                )}

                {/* On-page checker */}
                <div className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2.5 text-sm text-[#555A73]">
                    <Pencil size={14} strokeWidth={1.25} className="text-[#8B8FA7]" />
                    Vérificateur SEO On-Page
                    <span className="inline-block rounded-full bg-orion-primary/10 text-orion-primary px-1.5 py-0.5 text-[9px] font-semibold leading-none ml-1">
                      Bientôt
                    </span>
                  </div>
                  <span className="text-xs text-[#D1D5DB]">—</span>
                </div>
              </div>

              {/* Manual position */}
              <div className="rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] p-4">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={manualEnabled}
                    onChange={(e) => {
                      setManualEnabled(e.target.checked)
                      if (!e.target.checked) setManualValue('')
                      setSaved(false)
                      setSaveError(null)
                    }}
                    className="mt-0.5 accent-orion-primary"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#1B2559]">Définir une position manuellement</p>
                    <p className="text-xs text-[#8B8FA7] mt-0.5">
                      Utile quand la position affichée ne correspond pas à ce que vous observez réellement.
                    </p>
                  </div>
                </label>

                {manualEnabled && (
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="number"
                      min={1}
                      max={200}
                      value={manualValue}
                      onChange={(e) => { setManualValue(e.target.value); setSaved(false); setSaveError(null) }}
                      placeholder="Ex : 12"
                      className="w-24 rounded-lg border border-[#E2E4ED] bg-white px-3 py-1.5 text-sm text-[#1B2559] outline-none focus:border-orion-primary focus:ring-1 focus:ring-orion-primary/30 transition-colors"
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving || !manualValue}
                      className="flex items-center gap-1.5 rounded-lg bg-orion-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-orion-primary-hover disabled:opacity-50"
                    >
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    {saved && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <CheckCircle2 size={13} strokeWidth={1.5} />
                        Enregistré
                      </span>
                    )}
                    {saveError && (
                      <span className="flex items-center gap-1 text-xs text-red-500">
                        <AlertTriangle size={13} strokeWidth={1.5} />
                        {saveError}
                      </span>
                    )}
                  </div>
                )}

                {!manualEnabled && keyword.manualPosition !== null && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-amber-600">
                      Position manuelle active : <strong>{keyword.manualPosition}</strong>
                    </span>
                    <button
                      onClick={async () => {
                        setSaving(true)
                        try {
                          await onSaveManualPosition(keyword.id, null)
                          setSaved(true)
                          setTimeout(() => setSaved(false), 2000)
                        } catch {}
                        setSaving(false)
                      }}
                      className="text-xs text-[#8B8FA7] hover:text-red-500 transition-colors"
                    >
                      Effacer
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-[#F4F5FA] flex items-center justify-between">
              <span className="text-[11px] text-[#8B8FA7]">
                {keyword.device} · {keyword.location.toUpperCase()}
              </span>
              <button
                onClick={onClose}
                className="text-xs text-[#555A73] hover:text-[#1B2559] transition-colors"
              >
                Fermer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
